import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
    Flame, 
    DollarSign, 
    Gauge, 
    Activity, 
    TrendingUp, 
    AlertTriangle, 
    Lock,
    Droplets,
    BarChart3
} from 'lucide-react';
import { cn } from '../ui/utils';
import { 
    sanitizeNumericInput,
    PRODUCTION_VALIDATION_RULES 
} from '../../utils/validation/productionValidation';
import { FieldSyncIndicator } from './FieldSyncIndicator';
import type { FieldSyncStatus } from '../../types/draftEntry';

interface ProductionInputsProps {
    // Gas Section Fields (new schema)
    yesterdaysSales: string;
    gasFlowRate: string;
    staticPressure: string;
    differentialPressure: string;
    totalAccumulated: string;
    // Oil Section Fields
    totalSales: string;
    maintenanceCost: string;
    // Callbacks and state
    onFieldChange: (field: string, value: string) => void;
    onRetrySync?: (field: string) => void;
    fieldStates: Map<string, any>;
    fieldSyncStatuses?: Map<string, FieldSyncStatus>;
    isDarkMode: boolean;
    validationErrors?: Map<string, string>;
    validationWarnings?: Map<string, string>;
    isLocked?: boolean;
}

export function ProductionInputs({
    // Gas Section
    yesterdaysSales,
    gasFlowRate,
    staticPressure,
    differentialPressure,
    totalAccumulated,
    // Oil Section
    totalSales,
    maintenanceCost,
    // Common
    onFieldChange,
    onRetrySync,
    fieldStates,
    fieldSyncStatuses = new Map(),
    isDarkMode,
    validationErrors = new Map(),
    validationWarnings = new Map(),
    isLocked = false,
}: ProductionInputsProps) {

    const handleChange = useCallback((field: string, value: string) => {
        if (isLocked) return;
        const allowDecimal = !['staticPressure', 'differentialPressure'].includes(field);
        const sanitized = sanitizeNumericInput(value, allowDecimal, false);
        onFieldChange(field, sanitized);
    }, [onFieldChange, isLocked]);

    const getFieldError = (fieldName: string) => validationErrors.get(fieldName);
    const getFieldWarning = (fieldName: string) => validationWarnings.get(fieldName);
    const getFieldSyncStatus = (fieldName: string): FieldSyncStatus => 
        fieldSyncStatuses.get(fieldName) || 'pristine';

    const getStatusBorderColor = (fieldName: string, sectionColor: 'blue' | 'amber') => {
        const state = fieldStates.get(fieldName);
        const hasValue = state?.value !== undefined && state?.value !== '' && state?.value !== 0;
        const hasError = getFieldError(fieldName);
        const hasWarning = getFieldWarning(fieldName);
        const syncStatus = getFieldSyncStatus(fieldName);

        if (hasError) return 'border-red-500 ring-1 ring-red-500';
        if (hasWarning) return 'border-yellow-500 ring-1 ring-yellow-500';
        
        if (!hasValue) {
            return sectionColor === 'blue' 
                ? (isDarkMode ? 'border-cyan-900/50' : 'border-cyan-200')
                : (isDarkMode ? 'border-amber-900/50' : 'border-amber-200');
        }

        // Sync-based coloring for filled fields
        if (syncStatus === 'submitted' || syncStatus === 'synced') {
            return sectionColor === 'blue'
                ? (isDarkMode ? 'border-cyan-500 ring-1 ring-cyan-500/50' : 'border-cyan-500 ring-1 ring-cyan-500/50')
                : (isDarkMode ? 'border-amber-500 ring-1 ring-amber-500/50' : 'border-amber-500 ring-1 ring-amber-500/50');
        }
        if (syncStatus === 'local' || syncStatus === 'syncing') {
            return sectionColor === 'blue'
                ? (isDarkMode ? 'border-cyan-600/50 ring-1 ring-cyan-600/30' : 'border-cyan-400 ring-1 ring-cyan-400/50')
                : (isDarkMode ? 'border-amber-600/50 ring-1 ring-amber-600/30' : 'border-amber-400 ring-1 ring-amber-400/50');
        }
        if (syncStatus === 'error') {
            return 'border-red-500/50 ring-1 ring-red-500/50';
        }

        // Default valid and has value
        return sectionColor === 'blue'
            ? (isDarkMode ? 'border-cyan-500/50 ring-1 ring-cyan-500/30' : 'border-cyan-400 ring-1 ring-cyan-400/30')
            : (isDarkMode ? 'border-amber-500/50 ring-1 ring-amber-500/30' : 'border-amber-400 ring-1 ring-amber-400/30');
    };

    const inputClasses = (fieldName: string, sectionColor: 'blue' | 'amber') => cn(
        "w-full px-4 py-3 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2",
        sectionColor === 'blue' 
            ? (isDarkMode 
                ? "bg-slate-800 text-cyan-100 placeholder:text-cyan-800 focus:ring-cyan-500/50" 
                : "bg-white text-cyan-900 placeholder:text-cyan-300 focus:ring-cyan-500/50")
            : (isDarkMode 
                ? "bg-slate-800 text-amber-100 placeholder:text-amber-800 focus:ring-amber-500/50" 
                : "bg-white text-amber-900 placeholder:text-amber-300 focus:ring-amber-500/50"),
        getStatusBorderColor(fieldName, sectionColor),
        isLocked && "opacity-75 cursor-not-allowed"
    );

    const renderFieldMessage = (fieldName: string) => {
        const error = getFieldError(fieldName);
        const warning = getFieldWarning(fieldName);
        
        if (error) {
            return (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {error}
                </p>
            );
        }
        if (warning) {
            return (
                <p className="mt-1 text-xs text-yellow-500 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {warning}
                </p>
            );
        }
        return null;
    };

    const renderFieldLabel = (
        fieldName: string, 
        icon: React.ReactNode, 
        label: string, 
        sectionColor: 'blue' | 'amber',
        maxValue?: number
    ) => (
        <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
                {icon}
                <span className={cn(
                    "text-xs font-semibold uppercase tracking-wider",
                    sectionColor === 'blue'
                        ? (isDarkMode ? "text-cyan-400" : "text-cyan-700")
                        : (isDarkMode ? "text-amber-400" : "text-amber-700")
                )}>
                    {label}
                </span>
                {maxValue && (
                    <span className={cn(
                        "text-[10px] font-normal", 
                        isDarkMode ? "text-slate-600" : "text-gray-400"
                    )}>
                        max {maxValue.toLocaleString()}
                    </span>
                )}
                {isLocked && <Lock className="w-3 h-3 text-slate-400" />}
            </div>
            <FieldSyncIndicator
                status={getFieldSyncStatus(fieldName)}
                onRetry={onRetrySync ? () => onRetrySync(fieldName) : undefined}
                size="sm"
            />
        </div>
    );

    return (
        <div className="space-y-6">
            {/* ═══════════════════════════════════════════════════════════════
                GAS SECTION - Cyan/Blue Theme
            ═══════════════════════════════════════════════════════════════ */}
            <div className={cn(
                "rounded-2xl p-4 border",
                isDarkMode 
                    ? "bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-950 border-cyan-800/50" 
                    : "bg-gradient-to-br from-cyan-50 via-white to-blue-50 border-cyan-200"
            )}>
                {/* Section Header */}
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-cyan-500/20">
                    <div className={cn(
                        "p-2 rounded-xl",
                        isDarkMode ? "bg-cyan-900" : "bg-cyan-100"
                    )}>
                        <Flame className={cn("w-5 h-5", isDarkMode ? "text-cyan-400" : "text-cyan-600")} />
                    </div>
                    <div>
                        <h3 className={cn(
                            "text-sm font-bold uppercase tracking-wider",
                            isDarkMode ? "text-cyan-300" : "text-cyan-800"
                        )}>
                            Gas Production
                        </h3>
                        <p className={cn(
                            "text-xs",
                            isDarkMode ? "text-cyan-600" : "text-cyan-500"
                        )}>
                            Flow rates and pressure readings
                        </p>
                    </div>
                    {isLocked && <Lock className="w-4 h-4 text-cyan-500 ml-auto" />}
                </div>

                <div className="space-y-4">
                    {/* Row 1: Yesterday's Sales (first field) */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                    >
                        {renderFieldLabel(
                            'yesterdaysSales',
                            <DollarSign className={cn("w-4 h-4", isDarkMode ? "text-cyan-400" : "text-cyan-600")} />,
                            "Yesterday's Sales (MCF)",
                            'blue'
                        )}
                        <input
                            type="text"
                            inputMode="decimal"
                            value={yesterdaysSales}
                            onChange={(e) => handleChange('yesterdaysSales', e.target.value)}
                            className={inputClasses('yesterdaysSales', 'blue')}
                            placeholder="0.00"
                            disabled={isLocked}
                            readOnly={isLocked}
                        />
                        {renderFieldMessage('yesterdaysSales')}
                    </motion.div>

                    {/* Row 2: Gas Flow Rate */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        {renderFieldLabel(
                            'gasFlowRate',
                            <Activity className={cn("w-4 h-4", isDarkMode ? "text-cyan-400" : "text-cyan-600")} />,
                            'Gas Flow Rate (MCF/Day)',
                            'blue'
                        )}
                        <input
                            type="text"
                            inputMode="decimal"
                            value={gasFlowRate}
                            onChange={(e) => handleChange('gasFlowRate', e.target.value)}
                            className={inputClasses('gasFlowRate', 'blue')}
                            placeholder="0.00"
                            disabled={isLocked}
                            readOnly={isLocked}
                        />
                        {renderFieldMessage('gasFlowRate')}
                    </motion.div>

                    {/* Row 3: Static Pressure & Differential Pressure (side by side) */}
                    <div className="grid grid-cols-2 gap-3">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15 }}
                        >
                            {renderFieldLabel(
                                'staticPressure',
                                <Gauge className={cn("w-4 h-4", isDarkMode ? "text-cyan-400" : "text-cyan-600")} />,
                                'Static PSI',
                                'blue'
                            )}
                            <input
                                type="text"
                                inputMode="numeric"
                                value={staticPressure}
                                onChange={(e) => handleChange('staticPressure', e.target.value)}
                                className={inputClasses('staticPressure', 'blue')}
                                placeholder="0"
                                disabled={isLocked}
                                readOnly={isLocked}
                            />
                            {renderFieldMessage('staticPressure')}
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            {renderFieldLabel(
                                'differentialPressure',
                                <BarChart3 className={cn("w-4 h-4", isDarkMode ? "text-cyan-400" : "text-cyan-600")} />,
                                'Diff PSI',
                                'blue'
                            )}
                            <input
                                type="text"
                                inputMode="numeric"
                                value={differentialPressure}
                                onChange={(e) => handleChange('differentialPressure', e.target.value)}
                                className={inputClasses('differentialPressure', 'blue')}
                                placeholder="0"
                                disabled={isLocked}
                                readOnly={isLocked}
                            />
                            {renderFieldMessage('differentialPressure')}
                        </motion.div>
                    </div>

                    {/* Row 4: Total Accumulated */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25 }}
                    >
                        {renderFieldLabel(
                            'totalAccumulated',
                            <TrendingUp className={cn("w-4 h-4", isDarkMode ? "text-cyan-400" : "text-cyan-600")} />,
                            'Total Accumulated (MCF)',
                            'blue'
                        )}
                        <input
                            type="text"
                            inputMode="decimal"
                            value={totalAccumulated}
                            onChange={(e) => handleChange('totalAccumulated', e.target.value)}
                            className={inputClasses('totalAccumulated', 'blue')}
                            placeholder="0.00"
                            disabled={isLocked}
                            readOnly={isLocked}
                        />
                        {renderFieldMessage('totalAccumulated')}
                    </motion.div>
                </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════════
                OIL SECTION - Amber/Gold Theme
            ═══════════════════════════════════════════════════════════════ */}
            <div className={cn(
                "rounded-2xl p-4 border",
                isDarkMode 
                    ? "bg-gradient-to-br from-slate-900 via-slate-900 to-amber-950 border-amber-800/50" 
                    : "bg-gradient-to-br from-amber-50 via-white to-orange-50 border-amber-200"
            )}>
                {/* Section Header */}
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-amber-500/20">
                    <div className={cn(
                        "p-2 rounded-xl",
                        isDarkMode ? "bg-amber-900" : "bg-amber-100"
                    )}>
                        <Droplets className={cn("w-5 h-5", isDarkMode ? "text-amber-400" : "text-amber-600")} />
                    </div>
                    <div>
                        <h3 className={cn(
                            "text-sm font-bold uppercase tracking-wider",
                            isDarkMode ? "text-amber-300" : "text-amber-800"
                        )}>
                            Oil Production
                        </h3>
                        <p className={cn(
                            "text-xs",
                            isDarkMode ? "text-amber-600" : "text-amber-500"
                        )}>
                            Sales and maintenance costs
                        </p>
                    </div>
                    {isLocked && <Lock className="w-4 h-4 text-amber-500 ml-auto" />}
                </div>

                <div className="space-y-4">
                    {/* Total Sales */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        {renderFieldLabel(
                            'totalSales',
                            <DollarSign className={cn("w-4 h-4", isDarkMode ? "text-amber-400" : "text-amber-600")} />,
                            'Total Sales (BBL)',
                            'amber'
                        )}
                        <input
                            type="text"
                            inputMode="decimal"
                            value={totalSales}
                            onChange={(e) => handleChange('totalSales', e.target.value)}
                            className={inputClasses('totalSales', 'amber')}
                            placeholder="0.00"
                            disabled={isLocked}
                            readOnly={isLocked}
                        />
                        {renderFieldMessage('totalSales')}
                    </motion.div>

                    {/* Maintenance Cost */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.35 }}
                    >
                        {renderFieldLabel(
                            'maintenanceCost',
                            <WrenchIcon className={cn("w-4 h-4", isDarkMode ? "text-amber-400" : "text-amber-600")} />,
                            'Maintenance Cost ($)',
                            'amber'
                        )}
                        <input
                            type="text"
                            inputMode="decimal"
                            value={maintenanceCost}
                            onChange={(e) => handleChange('maintenanceCost', e.target.value)}
                            className={inputClasses('maintenanceCost', 'amber')}
                            placeholder="0.00"
                            disabled={isLocked}
                            readOnly={isLocked}
                        />
                        {renderFieldMessage('maintenanceCost')}
                    </motion.div>
                </div>
            </div>
        </div>
    );
}

// Wrench Icon Component
function WrenchIcon(props: React.SVGProps<SVGSVGElement>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            {...props}
        >
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
        </svg>
    );
}
